import { useCallback, useState } from 'react'
import { Empty, ErrorBanner, formatUtc, useAsync } from '../../components/Async'
import { Dialog } from '../../components/Dialog'
import { ApiError, type PlatformAdminSummary, platform, session } from '../../lib/api'

/**
 * The people who can run this deployment.
 *
 * A screen of its own rather than the Accounts screen pointed at a different organization, because
 * the rules are not the same ones. There is no role to choose - an account here holds exactly one -
 * and the protection against losing the last one is stricter than its tenant equivalent, for a
 * reason the screen says out loud: an organization that loses its last owner can be rescued by
 * somebody on this screen, and this screen has nobody above it.
 */
export function AdministratorListPage() {
  const roles = session.identity()?.roles ?? []
  if (!roles.includes('platform-admin')) {
    return (
      <div className="page-header">
        <div>
          <h1>Administrators</h1>
          <p>This screen belongs to whoever runs this deployment. Your account is not one of them.</p>
        </div>
      </div>
    )
  }

  return <PlatformAdminList />
}

function PlatformAdminList() {
  const [reloadToken, setReloadToken] = useState(0)
  const [adding, setAdding] = useState(false)
  const reload = useCallback(() => setReloadToken((token) => token + 1), [])
  const admins = useAsync(() => platform.listAdministrators(), [reloadToken])

  const onCreated = useCallback(() => {
    setAdding(false)
    reload()
  }, [reload])

  const activeCount = admins.value?.filter((admin) => admin.status === 'active').length ?? 0

  return (
    <>
      <div className="page-header">
        <div>
          <h1>Administrators</h1>
          <p>Who can create organizations, retire them, and open a customer&rsquo;s console.</p>
        </div>
        <button type="button" className="primary" onClick={() => setAdding(true)} data-testid="add-administrator">
          Add an administrator
        </button>
      </div>

      <ErrorBanner message={admins.error} />

      {/* The list of people is what this screen is for. The form for adding one is not shown until
          somebody asks for it, the way the customer-facing Accounts screen already does it. */}
      <AddAdministratorDialog open={adding} onClose={() => setAdding(false)} onCreated={onCreated} />

      <div className="card">
        <div className="card-body">
          <h3 style={{ marginTop: 0 }}>People</h3>

          {/*
            Said plainly, because it is the one number on this screen with a consequence. A
            deployment down to a single administrator has no second person who can reset a forgotten
            password, and no way back into itself if that account is lost.
          */}
          {activeCount === 1 && (
            <p className="error" data-testid="single-administrator">
              Only one administrator can sign in. If that account is lost, nobody can administer this
              deployment without editing the database. Add a second one.
            </p>
          )}

          <div style={{ overflowX: 'auto' }}>
            <table data-testid="administrator-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Status</th>
                  <th>Last signed in</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {admins.value?.map((admin) => (
                  <AdministratorRow
                    key={admin.accountId}
                    admin={admin}
                    onChanged={reload}
                    lastActive={activeCount === 1 && admin.status === 'active'}
                  />
                ))}
              </tbody>
            </table>
          </div>

          {admins.value && admins.value.length === 0 && (
            <Empty>
              {admins.loading
                ? 'Loading…'
                : 'This deployment has no platform organization. Run scripts/seed-platform.ps1.'}
            </Empty>
          )}
        </div>
      </div>
    </>
  )
}

function AdministratorRow({
  admin,
  onChanged,
  lastActive
}: {
  admin: PlatformAdminSummary
  onChanged: () => void
  lastActive: boolean
}) {
  const [problem, setProblem] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [resetting, setResetting] = useState(false)
  const [newPassword, setNewPassword] = useState('')
  const locked = admin.lockedUntilUtc !== null && new Date(admin.lockedUntilUtc) > new Date()

  const run = async (work: () => Promise<unknown>) => {
    setBusy(true)
    setProblem(null)
    try {
      await work()
      setResetting(false)
      setNewPassword('')
      onChanged()
    } catch (error) {
      setProblem(error instanceof ApiError ? error.message : 'That could not be done.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <tr data-testid={`administrator-${admin.email}`}>
      <td>
        {admin.displayName}
        <br />
        <span className="muted">{admin.email}</span>
        {problem && (
          <p className="error" role="alert" data-testid="administrator-problem">
            {problem}
          </p>
        )}
      </td>
      <td>
        {admin.status === 'active' ? 'Active' : 'Disabled'}
        {locked && (
          <>
            <br />
            <span className="muted">Locked until {formatUtc(admin.lockedUntilUtc)}</span>
          </>
        )}
        {admin.mustChangePassword && (
          <>
            <br />
            <span className="muted">Has not chosen a password yet</span>
          </>
        )}
      </td>
      <td>{admin.lastSignInUtc ? formatUtc(admin.lastSignInUtc) : 'Never signed in'}</td>
      <td>
        {/*
          Only while locked, and it does not rescue a *sole* locked-out administrator - they cannot
          sign in to press it, and for them the fifteen minutes remain the answer. It helps the
          two-administrator case, which is what the warning at the top of this screen is already
          pushing every deployment towards.
        */}
        {locked && (
          <>
            <button
              type="button"
              disabled={busy}
              data-testid={`unlock-administrator-${admin.email}`}
              onClick={() => run(() => platform.unlockAdministrator(admin.accountId))}
            >
              Unlock
            </button>{' '}
          </>
        )}
        {/*
          Disabled rather than hidden when it is the last one, so the reason is visible rather than
          the control simply being absent. The server refuses it either way.
        */}
        <button
          type="button"
          disabled={busy || lastActive}
          title={lastActive ? 'This is the only administrator who can sign in.' : undefined}
          onClick={() =>
            run(() =>
              platform.setAdministratorStatus(
                admin.accountId,
                admin.status === 'active' ? 'disabled' : 'active'
              )
            )
          }
        >
          {admin.status === 'active' ? 'Disable' : 'Enable'}
        </button>{' '}
        <button
          type="button"
          disabled={busy}
          onClick={() => setResetting(true)}
          data-testid={`reset-administrator-${admin.email}`}
        >
          Reset password
        </button>

        {/*
          A dialog rather than a form unfolding in the cell, which is what this was. The Accounts
          screen made the same move and says why: a form opening inside a table cell reflows every
          row below it, so the person somebody was aiming at moves as they reach for it. `CONSOLE.md`
          has claimed since D-421 that every screen here asks this way; this was the screen where
          that was still untrue.
        */}
        <Dialog
          open={resetting}
          title={`Reset the password for ${admin.displayName}`}
          onClose={() => setResetting(false)}
          data-testid={`reset-admin-dialog-${admin.email}`}
        >
          <form
            onSubmit={(event) => {
              event.preventDefault()
              void run(() => platform.resetAdministratorPassword(admin.accountId, newPassword))
            }}
          >
            <label htmlFor={`reset-admin-${admin.accountId}`}>
              A new password for {admin.displayName}. They must replace it at first use.
            </label>
            {/* Readable as it is typed, for the reason the create field states: whoever resets a
                password has to say it out loud to the person it belongs to, and it cannot survive
                first use either way. `off` rather than `new-password` because this is somebody
                else's secret and the browser would file it under the administrator's own login. */}
            <input
              id={`reset-admin-${admin.accountId}`}
              type="text"
              autoComplete="off"
              value={newPassword}
              onChange={(event) => setNewPassword(event.target.value)}
              data-testid={`reset-administrator-password-${admin.email}`}
              required
            />

            {/* Inside the dialog, beside the field it is about. A refusal rendered on the page
                behind would be hidden by the thing that caused it (D-388). */}
            {problem && (
              <p className="error" role="alert" data-testid={`reset-admin-problem-${admin.email}`}>
                {problem}
              </p>
            )}

            <div className="dialog-actions">
              <button type="button" onClick={() => setResetting(false)} disabled={busy}>
                Cancel
              </button>
              <button
                type="submit"
                className="primary"
                disabled={busy}
                data-testid={`confirm-reset-admin-${admin.email}`}
              >
                Set password
              </button>
            </div>
          </form>
        </Dialog>
      </td>
    </tr>
  )
}

function AddAdministratorDialog({
  open,
  onClose,
  onCreated
}: {
  open: boolean
  onClose: () => void
  onCreated: () => void
}) {
  const [email, setEmail] = useState('')
  const [displayName, setDisplayName] = useState('')
  const [password, setPassword] = useState('')
  const [problem, setProblem] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)

  const submit = async (event: React.FormEvent) => {
    event.preventDefault()
    setSaving(true)
    setProblem(null)
    try {
      await platform.createAdministrator(email, displayName || email, password)
      setEmail('')
      setDisplayName('')
      setPassword('')
      onCreated()
    } catch (error) {
      setProblem(error instanceof ApiError ? error.message : 'The administrator could not be created.')
    } finally {
      setSaving(false)
    }
  }

  return (
    <Dialog open={open} title="Add an administrator" onClose={onClose} data-testid="create-administrator-dialog">
      <p className="muted" style={{ marginTop: 0 }}>
        They will be able to create and retire organizations, and open any customer&rsquo;s console.
        You choose their first password; they have to replace it before they can use anything.
      </p>
      <form onSubmit={submit} data-testid="create-administrator-form">
        <label htmlFor="admin-email">Email address</label>
        <input
          id="admin-email"
          type="email"
          value={email}
          onChange={(event) => setEmail(event.target.value)}
          required
        />

        <label htmlFor="admin-name">Name</label>
        <input
          id="admin-name"
          value={displayName}
          onChange={(event) => setDisplayName(event.target.value)}
          placeholder="Shown against anything they do, including in a customer's audit trail"
        />

        <label htmlFor="admin-password">First password</label>
        <input
          id="admin-password"
          type="text"
          autoComplete="off"
          value={password}
          onChange={(event) => setPassword(event.target.value)}
          required
        />

        {problem && (
          <p className="error" role="alert" data-testid="create-administrator-error">
            {problem}
          </p>
        )}

        <div className="dialog-actions">
          <button type="button" onClick={onClose} disabled={saving}>
            Cancel
          </button>
          <button type="submit" className="primary" disabled={saving}>
            {saving ? 'Creating…' : 'Create administrator'}
          </button>
        </div>
      </form>
    </Dialog>
  )
}
